import Modal from 'modules/common/components/Modal';
import { DISCOUNT_VALUE_TYPES, PRODUCT_TYPE } from '../../constants';
import type { DiscountStatus } from '../../types/discount.types';
import discountsStrings from '../../strings';
import DiscountStatusBadge from '../DiscountStatusBadge/DiscountStatusBadge';
import SelectProductsModal from '../SelectProductsModal/SelectProductsModal';
import { useCreateDiscountForm } from './useCreateDiscountForm';
import {
  CreateDiscountTopRow,
  DateRangeRow,
  InfoBanner,
  InlineError,
  ModalBodyContent,
  ModalField,
  ModalInput,
  ModalLabel,
  ModalSelect,
  OfferDiscountInputWrap,
  OfferTable,
  OfferTableHeader,
  OfferTableRow,
  OfferTableScroll,
  RemoveLineButton,
  SelectProductsLinkButton,
} from '../../views/Discounts.style';

const today = new Date().toISOString().slice(0, 10);

interface CreateDiscountModalProps {
  isOpen: boolean;
  isSubmitting: boolean;
  onClose: () => void;
}

const CreateDiscountModal = ({ isOpen, isSubmitting, onClose }: CreateDiscountModalProps) => {
  const {
    register,
    errors,
    fields,
    watchedStartDate,
    offerLines,
    draftProducts,
    isSelectProductsOpen,
    handleOpenSelectProducts,
    handleToggleDraftProduct,
    handleCloseSelectProducts,
    handleApplySelectedProducts,
    handleRemoveLine,
    handleFormSubmit,
  } = useCreateDiscountForm({ isOpen });

  return (
    <>
      <Modal
        toggle={isOpen}
        title={discountsStrings.createDiscountTitle}
        onClose={onClose}
        onCancelHandler={onClose}
        cancelActionLabel={discountsStrings.cancelBtn}
        onConfirmHandler={handleFormSubmit}
        confirmActionLabel={discountsStrings.createOfferSubmitBtn}
        confirmActionLoading={isSubmitting}
        disableConfirmButton={offerLines.length === 0}
        closeOnClickOutside={false}
        size="lg"
      >
        <ModalBodyContent $fixedHeight>
          <CreateDiscountTopRow>
            <ModalField>
              <ModalLabel>{discountsStrings.selectProductsMultipleLabel}</ModalLabel>
              <ModalInput value={String(offerLines.length)} readOnly />
            </ModalField>
            <SelectProductsLinkButton type="button" onClick={handleOpenSelectProducts}>
              {discountsStrings.selectProductsBtn}
            </SelectProductsLinkButton>
          </CreateDiscountTopRow>

          <DateRangeRow>
            <ModalField>
              <ModalLabel>{discountsStrings.fromLabel}</ModalLabel>
              <ModalInput type="date" min={today} {...register('startDate')} />
              {errors.startDate && <InlineError>{errors.startDate.message}</InlineError>}
            </ModalField>
            <ModalField>
              <ModalLabel>{discountsStrings.toLabel}</ModalLabel>
              <ModalInput
                type="date"
                min={watchedStartDate || today}
                {...register('endDate')}
              />
              {errors.endDate && <InlineError>{errors.endDate.message}</InlineError>}
            </ModalField>
          </DateRangeRow>

          {fields.length > 0 ? (
            <OfferTableScroll>
              <OfferTable>
                <OfferTableHeader>
                  <span>{discountsStrings.productsSummaryLabel}</span>
                  <span>{discountsStrings.statusColumnLabel}</span>
                  <span>{discountsStrings.originalPriceLabel}</span>
                  <span>{discountsStrings.discountColumnLabel}</span>
                  <span>{discountsStrings.actionsColumnLabel}</span>
                </OfferTableHeader>

                {fields.map((field, index) => {
                  const line = offerLines[index];
                  return (
                    <OfferTableRow key={field.id}>
                      <span>{line?.productName ?? field.productKey}</span>
                      <span>
                        {line?.existingOfferStatus ? (
                          <DiscountStatusBadge status={line.existingOfferStatus as DiscountStatus} />
                        ) : (
                          '-'
                        )}
                      </span>
                      <span>{line?.originalPrice.toFixed(2) ?? '0.00'}</span>
                      <OfferDiscountInputWrap>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                          <div style={{ display: 'flex', gap: '4px' }}>
                            <input type="hidden" {...register(`lines.${index}.productKey`)} />
                            <input
                              type="hidden"
                              {...register(`lines.${index}.productShapeId`, { valueAsNumber: true })}
                            />
                            <ModalInput
                              type="number"
                              min="0"
                              step="any"
                              placeholder={discountsStrings.discountPercentagePlaceholder}
                              {...register(`lines.${index}.discountValue`)}
                            />
                            <ModalSelect {...register(`lines.${index}.discountType`)}>
                              <option value={DISCOUNT_VALUE_TYPES.Percent}>%</option>
                              <option value={DISCOUNT_VALUE_TYPES.Amount}>
                                {discountsStrings.currency}
                              </option>
                            </ModalSelect>
                          </div>
                          {errors.lines?.[index]?.discountValue && (
                            <InlineError>{errors.lines[index].discountValue.message}</InlineError>
                          )}
                        </div>
                      </OfferDiscountInputWrap>
                      <RemoveLineButton
                        type="button"
                        onClick={() => handleRemoveLine(field.productKey, field.productShapeId)}
                      >
                        🗑
                      </RemoveLineButton>
                    </OfferTableRow>
                  );
                })}
              </OfferTable>
            </OfferTableScroll>
          ) : null}

          <InfoBanner>{discountsStrings.discountInfoBanner}</InfoBanner>
        </ModalBodyContent>
      </Modal>

      <SelectProductsModal
        isOpen={isSelectProductsOpen}
        stacked
        selectedProducts={draftProducts}
        onToggleProduct={handleToggleDraftProduct}
        onClose={handleCloseSelectProducts}
        onSave={handleApplySelectedProducts}
        productType={PRODUCT_TYPE.Product}
      />
    </>
  );
};

export default CreateDiscountModal;
